import * as React from 'react';
import { useMemo } from 'react';
import { DirectoryRater, RaterLevel, AddedDirectoryRater } from '../models';
import { TextDirection } from '../../../../localization/textDirection';
import { css } from '@emotion/core';
import RaterSearchTableRow from './RaterSearchTableRow';
import { COLORS } from '../../../../globalStyle/colors';
import { FIELDSET_DEFAULT } from '../../../../layout/fieldSetStyle';
import LocalDiv from '../../../../localization/LocalDiv';
import { useWindowSize } from '../../../../core/hooks/useWindowSize';
import RaterSearchCard from './RaterSearchCard';
import PaginatedScroll from '../../../../layout/PaginatedScroll';
import LocalBtn from '../../../../localization/LocalBtn';
import { btnStyle } from '../../../../forms/styles/button';
import { FLEX_ROW } from '../../../../layout/flexBox';
import { onMobileMedium } from '../../../../globalStyle/mediaQueries';
import { useMsgText } from '../../../../localization/hooks';
import { Str } from '../../../../localization/translationKeys';


const styles = {
  table:               css`
    table-layout: auto;
    width: 100%;
    border-collapse: collapse;
    background-color: ${COLORS.LIGHTEST_GREY};

    & td {
      padding: 18px;
      border: 1px solid;
      border-color: ${COLORS.LIGHTER_GREY} ${COLORS.LIGHTEST_GREY};
    }
  `,
  cards:               css`
    padding: 18px 12px;
    background-color: ${COLORS.LIGHTEST_GREY};
    border: 1px solid;
    border-color: ${COLORS.LIGHTER_GREY} ${COLORS.LIGHTEST_GREY} ${COLORS.LIGHTEST_GREY};
  `,
  loadButtonContainer: {
    root:   css`
      ${onMobileMedium} {
        justify-content: stretch;
      }
    `,
    button: css`
      padding-right: 30px;
      padding-left: 30px;

      ${onMobileMedium} {
        flex-basis: 100%;
      }
    `,
  },
};


type Props = {
  className?: string;
  dir: TextDirection;
  raterLevels: RaterLevel[];
  searchResults: DirectoryRater[];
  setAddedRater: (addedRater?: AddedDirectoryRater) => void;
  resultsPageSize: number;
  lastLoadedResultsPage: number;
  loadMoreResults: () => void;
};


/**
 * RaterSearchResultList: a component that renders the list of raters returned from search, as rows in a table.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const RaterSearchResultList: React.FC<Props> = props => {
  // Destructure to resolve a linting warning in the useMemo call below
  const { setAddedRater, ...rest } = props;

  const loadMoreBtnText = useMsgText(Str.LoadMore);

  const { isMobile } = useWindowSize();
  const lastPage     = useMemo(
    () => props.searchResults.length / props.resultsPageSize,
    [props.resultsPageSize, props.searchResults.length],
  );

  const mapResultsToRows = useMemo(
    () => rest.searchResults.map(result => (
        <RaterSearchTableRow
          key={result.personId}
          dir={rest.dir}
          rater={result}
          raterLevels={rest.raterLevels}
          setAddedRater={(levelId: number) => setAddedRater({ levelId, ...result })}
        />
      ),
    ),
    [rest.dir, rest.raterLevels, rest.searchResults, setAddedRater],
  );

  const mapResultsToCards = useMemo(
    () => rest.searchResults.map(result => (
        <RaterSearchCard
          key={result.personId}
          dir={rest.dir}
          rater={result}
          raterLevels={rest.raterLevels}
          setAddedRater={(levelId: number) => setAddedRater({ levelId, ...result })}
          css={styles.cards}
        />
      ),
    ),
    [rest.dir, rest.raterLevels, rest.searchResults, setAddedRater],
  );

  const onClickLoadBtn = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    props.loadMoreResults();
  };

  return (
    <LocalDiv className={rest.className}>
      <fieldset css={FIELDSET_DEFAULT}>
        <form>
          {
            isMobile
              ? (
                <>
                  <PaginatedScroll
                    pageSize={props.resultsPageSize}
                    currentPage={props.lastLoadedResultsPage}
                  >
                    {mapResultsToCards}
                  </PaginatedScroll>
                  {
                    props.searchResults.length > props.resultsPageSize && (
                      <LocalDiv css={[FLEX_ROW, styles.cards, styles.loadButtonContainer.root]}>
                        <LocalBtn
                          str=''
                          defaultText={loadMoreBtnText}
                          css={[btnStyle.primary(COLORS.BLUE), styles.loadButtonContainer.button]}
                          onClick={onClickLoadBtn}
                          disabled={props.lastLoadedResultsPage > lastPage}
                        />
                      </LocalDiv>
                    )
                  }
                </>
              ) : (
                <table css={styles.table}>
                  <tbody>
                    <PaginatedScroll
                      pageSize={props.resultsPageSize}
                      currentPage={props.lastLoadedResultsPage}
                    >
                      {mapResultsToRows}
                    </PaginatedScroll>
                    {
                      props.searchResults.length > props.resultsPageSize && (
                        <tr>
                          <td />
                          <td>
                            <LocalBtn
                              str=''
                              defaultText={loadMoreBtnText}
                              css={btnStyle.primary(COLORS.BLUE)}
                              onClick={onClickLoadBtn}
                              disabled={props.lastLoadedResultsPage > lastPage}
                            />
                          </td>
                          <td />
                        </tr>
                      )
                    }
                  </tbody>
                </table>
              )
          }
        </form>
      </fieldset>
    </LocalDiv>
  );
};

export default RaterSearchResultList;
